/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Argon2id wrapper. Wraps `@noble/hashes/argon2.js` to provide:
 *   - a single seam for benchmarking and parameter tuning
 *   - a consistent async API across all crypto/* primitives (a future
 *     wasm-backed or worker-offloaded implementation drops in without
 *     touching call sites)
 */

import { argon2id as nobleArgon2id } from '@noble/hashes/argon2.js';

const MINIMUM_MEMORY_KIB = 8;
const MINIMUM_OUTPUT_LENGTH = 4;

/**
 * The F4-blessed Argon2id parameters for browser use: 64 MiB / 3
 * iterations / single lane / 32-byte output. Sized so that an unlock
 * takes ~1–1.5 s on a modern laptop while still providing strong
 * memory-hardness against GPU/ASIC attackers.
 *
 * Reference values only — `argon2id()` does **not** apply defaults
 * (every field of `Argon2idParams` is required, by design: crypto
 * primitives should reject implicit costs). Call sites that want the
 * defaults should fold them in themselves; see
 * `src/services/vault/envelope.ts` for the canonical pattern
 * (`params.kdfParams?.memoryKiB ?? ARGON2ID_DEFAULTS.memoryKiB`).
 */
export const ARGON2ID_DEFAULTS: Readonly<{
  memoryKiB: number;
  iterations: number;
  parallelism: number;
  outputLength: number;
}> = Object.freeze({
  memoryKiB: 65536,
  iterations: 3,
  parallelism: 1,
  outputLength: 32,
});

export interface Argon2idParams {
  /** Passphrase bytes. Strings are UTF-8 encoded. */
  passphrase: Uint8Array | string;
  /** Per-key salt. Must be non-empty; 16 bytes is conventional. */
  salt: Uint8Array;
  /** Memory cost in KiB. */
  memoryKiB: number;
  /** Iteration (time) cost. */
  iterations: number;
  /** Parallelism (lanes). */
  parallelism: number;
  /** Derived-key length in bytes. */
  outputLength: number;
}

/**
 * Thrown when Argon2id is invoked with parameters outside the allowed
 * range (empty salt, sub-minimal memory, zero iterations, etc.). Subtype
 * of `Error` for instanceof checks.
 */
export class InvalidParameterError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidParameterError';
  }
}

/**
 * Derives a key from a passphrase using Argon2id.
 *
 * Async signature even though the underlying primitive is synchronous —
 * a future swap to a worker-offloaded or wasm implementation will be
 * source-compatible at every call site.
 *
 * @param params - passphrase, salt, and Argon2id cost parameters
 * @returns derived key bytes of length `params.outputLength`
 * @throws {InvalidParameterError} on out-of-range parameters
 */
/**
 * Validate that `value` is a finite integer at least `min`. `<` magnitude
 * checks alone are not enough: `NaN < n` is `false` and `Infinity < n`
 * is `false` for any finite `n`, so the magnitude check used to let
 * `NaN`, `Infinity`, and fractional values like `3.5` slip through into
 * noble (#104). `Number.isInteger` returns `false` for all three in a
 * single call.
 */
const requireIntegerAtLeast = (
  value: number,
  min: number,
  name: string
): void => {
  if (!Number.isInteger(value)) {
    throw new InvalidParameterError(
      `argon2id: ${name} must be a finite integer, got ${value}`
    );
  }
  if (value < min) {
    throw new InvalidParameterError(
      `argon2id: ${name} must be >= ${min}, got ${value}`
    );
  }
};

export const argon2id = async (params: Argon2idParams): Promise<Uint8Array> => {
  const { passphrase, salt, memoryKiB, iterations, parallelism, outputLength } =
    params;

  if (salt.length === 0) {
    throw new InvalidParameterError('argon2id: salt must not be empty');
  }
  requireIntegerAtLeast(memoryKiB, MINIMUM_MEMORY_KIB, 'memoryKiB');
  requireIntegerAtLeast(iterations, 1, 'iterations');
  requireIntegerAtLeast(parallelism, 1, 'parallelism');
  requireIntegerAtLeast(outputLength, MINIMUM_OUTPUT_LENGTH, 'outputLength');

  const passphraseBytes =
    typeof passphrase === 'string'
      ? new TextEncoder().encode(passphrase)
      : passphrase;

  return nobleArgon2id(passphraseBytes, salt, {
    m: memoryKiB,
    t: iterations,
    p: parallelism,
    dkLen: outputLength,
  });
};
